/**
 * Suggested Actions Panel
 *
 * Display AI-suggested actions with one-click execution
 */

import { Card, Button, Space, Typography, Tag, Tooltip } from 'antd';
import {
  ThunderboltOutlined,
  FileAddOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
  MailOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { SuggestedAction } from '../../types/ai';
import useAIStore from '../../store/aiStore';
import useThemeStore from '../../store/themeStore';
import { completeAction } from '../../api/ai';
import { message } from 'antd';

const { Text } = Typography;

// Map action types to icons
const ACTION_ICONS: Record<string, React.ReactNode> = {
  create_invoice: <FileAddOutlined />,
  record_payment: <DollarOutlined />,
  follow_up: <ClockCircleOutlined />,
  review_report: <BarChartOutlined />,
  update_inventory: <ShoppingOutlined />,
  approve_order: <CheckCircleOutlined />,
  send_reminder: <MailOutlined />,
  reconcile_account: <CheckCircleOutlined />,
};

// Map priority to color
const PRIORITY_COLORS: Record<string, string> = {
  high: '#ff4d4f',
  medium: '#faad14',
  low: '#52c41a',
};

interface SuggestedActionsPanelProps {
  actions: SuggestedAction[];
}

export default function SuggestedActionsPanel({ actions }: SuggestedActionsPanelProps) {
  const { removeSuggestedAction } = useAIStore();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const handleExecuteAction = async (action: SuggestedAction) => {
    try {
      if (action.action) {
        await action.action();
      }
      // Mark action as completed in API
      await completeAction(action.id, 'Action executed by user');
      removeSuggestedAction(action.id);
      message.success('Action completed successfully');
    } catch (error) {
      console.error('Failed to execute action:', error);
      message.error('Failed to execute action');
    }
  };

  const handleDismiss = (actionId: string) => {
    removeSuggestedAction(actionId);
    message.success('Action dismissed');
  };

  if (actions.length === 0) return null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#faad14' }} />
          <Text strong>Suggested Actions</Text>
        </Space>
      }
      styles={{ body: { padding: '8px 12px' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {actions.map((action) => (
          <div
            key={action.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px',
              background: isDark ? '#1f1f1f' : '#fafafa',
              borderRadius: 6,
              border: `1px solid ${PRIORITY_COLORS[action.priority]}20`,
            }}
          >
            <div style={{ fontSize: 20, color: '#1890ff', flexShrink: 0, marginTop: 2 }}>
              {ACTION_ICONS[action.type] || <ThunderboltOutlined />}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space size="small" wrap>
                  <Text strong style={{ fontSize: 13 }}>
                    {action.title}
                  </Text>
                  <Tag
                    color={PRIORITY_COLORS[action.priority]}
                    style={{ fontSize: 10, padding: '0 4px', margin: 0 }}
                  >
                    {action.priority.toUpperCase()}
                  </Tag>
                </Space>

                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {action.description}
                </Text>

                {action.context.reason && (
                  <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', display: 'block' }}>
                    {action.context.reason}
                  </Text>
                )}

                <Space size="small" style={{ marginTop: 4 }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleExecuteAction(action)}
                  >
                    Take Action
                  </Button>
                  <Tooltip title="Dismiss">
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleDismiss(action.id)}
                    />
                  </Tooltip>
                </Space>
              </Space>
            </div>
          </div>
        ))}
      </Space>
    </Card>
  );
}
